<script setup lang="ts">
// Providers
import { useCalculationTypes } from "@/dataProviders/CalculationTypes";

// Types
import { WizardProjectType } from "@/features/BuildingProject";

// Utils
import { specialConditionsApply, standardValuesApplies } from "@/features/BuildingProject/utils/wizardStepConditions";

// Components
import SpecialConditions from "../SpecialConditions/SpecialConditions.vue";
import StandardValues from "../StandardValues/StandardValues.vue";
import StepIntro from "../StepIntro/StepIntro.vue";

/**
 * Standard values and special conditions on one screen, as the concessions the regulation grants.
 *
 * They were two steps, and both were a heading, a toggle and some explanatory prose. To a user they are one
 * subject: things the rules let you claim. Some evidence the domain agrees — Special conditions' own info
 * button already pointed at the Standard values document.
 *
 * The cheapest merge in the create flow, and deliberately done first for that reason: both steps'
 * `valid()` returned a constant `true`, so combining them cannot break validation. There is none.
 *
 * Each half keeps its own condition, because they are not the same condition. Standard values turn on the
 * calculation type's variant (Denmark) or its id (Finland) and are always on elsewhere; special conditions
 * turn on the type carrying any `specialConditionTypes` at all. Both predicates are shared with
 * `createWizardSteps`, which uses their disjunction to decide whether this step exists — so a project where
 * neither applies gets no empty screen, it gets one step fewer.
 *
 * Each half now carries its own card (icon, heading, its own activation Switch), so there is nothing left
 * for this screen to render beyond the shared question and the two children — no divider needed between
 * them either, since each half's card border already separates it from the other. Same precedent
 * BuildingWithReferenceArea sets for its own two cards.
 */
const props = defineProps<{
  wasValidated: boolean;
  project: WizardProjectType;
}>();

const calculationTypes = useCalculationTypes();

/**
 * Looked up here and in `createWizardSteps`, which is a lookup rather than a rule, so two callers are fine.
 * The rules themselves live in one place precisely so they cannot drift.
 */
const selectedCalculationType = () => {
  const id = props.project.emissionCalculationAssumptions?.calculationType?.calculationTypeEnum;
  return id === null || id === undefined ? undefined : calculationTypes.getSelectedCalculationType(id);
};

const showStandardValues = () => standardValuesApplies(props.project, selectedCalculationType());
const showSpecialConditions = () => specialConditionsApply(props.project, selectedCalculationType());
</script>

<template>
  <div class="flex w-full flex-col">
    <StepIntro
      class="mb-6"
      heading="Do standard values or special conditions apply?"
      hint="Standard values stand in where a material has no validated EPD. Special conditions give documented special circumstances relief in the assessment." />

    <div class="flex w-full flex-col gap-3">
      <StandardValues
        v-if="showStandardValues()"
        :project="project"
        :was-validated="wasValidated"
        :hide-intro="true" />

      <SpecialConditions
        v-if="showSpecialConditions()"
        :project="project"
        :was-validated="wasValidated"
        :hide-intro="true" />
    </div>
  </div>
</template>
